import { z } from 'zod';

import { diffArtifactContent } from './artifact-diff.ts';
import {
  type ArtifactContentByKind,
  type DraftArtifactKind,
  STALE_UPSTREAM_KINDS,
  type StaleUpstreamKind,
} from './artifacts.ts';
import { canonicalJson } from './canonical-json.ts';

/**
 * Why a reconciled version says what it says, as `artifact_versions.change_notes` stores it.
 *
 * *What* changed is never stored: it is the diff between the version and the one before it, computed
 * on read, so it cannot disagree with the content. What is stored is *why*: the upstream kinds the
 * version was reconciled against, whether it changed anything at all, and a reason for each item the
 * reconciler changed, added or removed. Stored beside the content rather than in it, so a reason is
 * not part of the content hash or of anything keyed on the content.
 */
export const CHANGE_NOTES_SCHEMA_VERSION = 1;

/** The longest reason one item carries. */
export const MAX_CHANGE_NOTE_REASON_CHARS = 140;

/**
 * A reason as a reconciler gives it, cut at the last word that fits a change note rather than
 * refused: a long explanation costs the researcher a few words, never the whole reconciliation.
 */
export const changeReasonSchema = z
  .string()
  .trim()
  .min(1)
  .transform((reason) => {
    if (reason.length <= MAX_CHANGE_NOTE_REASON_CHARS) {
      return reason;
    }

    const cut = reason.slice(0, MAX_CHANGE_NOTE_REASON_CHARS - 1);
    const space = cut.lastIndexOf(' ');

    return `${(space > 0 ? cut.slice(0, space) : cut).trimEnd()}…`;
  });

const changeNoteSchema = z.object({
  id: z.uuid(),
  reason: z.string().trim().min(1).max(MAX_CHANGE_NOTE_REASON_CHARS),
});

export const artifactChangeNotesSchema = z
  .object({
    schema_version: z.literal(CHANGE_NOTES_SCHEMA_VERSION),
    /** Empty only when neither the writer nor the windows the write cleared named a kind. */
    upstream_kinds: z.array(z.enum(STALE_UPSTREAM_KINDS)),
    /** True when the version's content is the content of the version before it. */
    unchanged: z.boolean(),
    items: z.array(changeNoteSchema),
  })
  .superRefine((notes, context) => {
    if (new Set(notes.upstream_kinds).size !== notes.upstream_kinds.length) {
      context.addIssue({ code: 'custom', path: ['upstream_kinds'], message: 'one entry per kind' });
    }

    if (new Set(notes.items.map((item) => item.id)).size !== notes.items.length) {
      context.addIssue({ code: 'custom', path: ['items'], message: 'one reason per item' });
    }
  });
export type ArtifactChangeNotes = z.infer<typeof artifactChangeNotesSchema>;

/**
 * Stored notes, or `null` for none and for a value this build cannot read.
 *
 * A note is an explanation beside a version, never something a decision rests on, so a value that
 * does not parse costs the reader a reason line rather than the whole step.
 */
export const parseChangeNotes = (value: unknown): ArtifactChangeNotes | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsed = artifactChangeNotesSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
};

/** What a reconciler says about the version it writes; see `changeNotesFor`. */
export interface ChangeNotesInput {
  upstream_kinds: readonly StaleUpstreamKind[];
  items: readonly { id: string; reason: string }[];
}

const upstreamOrder = (kinds: readonly StaleUpstreamKind[]): StaleUpstreamKind[] =>
  STALE_UPSTREAM_KINDS.filter((kind) => kinds.includes(kind));

/** A reason as stored: trimmed, and cut to the longest a note keeps; `null` for one with no words. */
const storedReason = (reason: unknown): string | null => {
  if (typeof reason !== 'string') {
    return null;
  }

  const trimmed = reason.trim();
  if (trimmed === '') {
    return null;
  }

  return trimmed.length <= MAX_CHANGE_NOTE_REASON_CHARS
    ? trimmed
    : `${trimmed.slice(0, MAX_CHANGE_NOTE_REASON_CHARS - 1).trimEnd()}…`;
};

/**
 * The notes a version is stored with, from what its writer said about it and what it really changed.
 *
 * A reason is kept only for an item the diff between the two versions reports as added, changed or
 * removed: a reason for an item that did not change would mark it changed on the step when nothing
 * about it moved. `unchanged` is read off the content rather than taken from the writer, for the same
 * reason. Upstream kinds are stored in the order a change travels; when the writer names none, they
 * are the kinds of the windows the write cleared (`answered`).
 *
 * Never throws. It runs inside the write that stores the version, and a note is an explanation
 * beside the version rather than part of it, so what the record cannot hold is dropped rather than
 * refusing the version: an item whose reason has no words, or whose id the diff does not report
 * (which an id that is not one never is), and an upstream kind that is not one, which
 * `upstreamOrder` leaves out. A reason past `MAX_CHANGE_NOTE_REASON_CHARS` is cut to it.
 */
export const changeNotesFor = <Kind extends DraftArtifactKind>({
  kind,
  previous,
  next,
  notes,
  answered = [],
}: {
  kind: Kind;
  previous: ArtifactContentByKind[Kind];
  next: ArtifactContentByKind[Kind];
  notes: ChangeNotesInput;
  /** The kinds of the windows the write cleared. */
  answered?: readonly StaleUpstreamKind[] | undefined;
}): ArtifactChangeNotes => {
  const diff = diffArtifactContent(kind, previous, next);
  const reported = new Set(
    [...diff.added, ...diff.changed, ...diff.removed].map((item) => item.id),
  );
  const kept = new Map<string, string>();
  for (const item of notes.items) {
    const reason = storedReason(item.reason);
    if (reason !== null && reported.has(item.id) && !kept.has(item.id)) {
      kept.set(item.id, reason);
    }
  }
  const named = upstreamOrder(notes.upstream_kinds);

  return {
    schema_version: CHANGE_NOTES_SCHEMA_VERSION,
    upstream_kinds: named.length > 0 ? named : upstreamOrder(answered),
    unchanged: canonicalJson(previous) === canonicalJson(next),
    items: [...kept].map(([id, reason]) => ({ id, reason })),
  };
};

/**
 * What a step shows of a reconciled version; see `describeReconciliation`. A schema as well as a
 * type because the SPA reads it off the step views.
 */
export const reconciliationViewSchema = z.object({
  version: z.int().positive(),
  upstreamKinds: z.array(z.enum(STALE_UPSTREAM_KINDS)),
  unchanged: z.boolean(),
  items: z.array(
    z.object({
      id: z.string(),
      change: z.enum(['added', 'changed', 'removed']),
      /** The item as it reads in the reconciled version, or as it last read for a removed one. */
      label: z.string(),
      /** Why it changed, as the reconciler said; `null` where it gave none. */
      reason: z.string().nullable(),
    }),
  ),
  /**
   * The fields of the document itself the version changed — the audience's description, count or
   * countries — which no item carries; absent where it changed none. They have no reason of
   * their own: the version's line is what explains them.
   */
  changedFields: z.array(z.string()).optional(),
  /**
   * Set on a guide redrafted over a coverage gap or uncertain line (`redrafted_for_coverage`),
   * which answers the check's lines rather than a change above it, so its `upstreamKinds` is empty; absent otherwise.
   */
  repair: z.literal(true).optional(),
});
export type ReconciliationView = z.infer<typeof reconciliationViewSchema>;
export type ReconciliationItem = ReconciliationView['items'][number];

/**
 * A reconciled version as its step shows it: what it was reconciled against, and each item it
 * changed with the reason given for it, and the fields of the document itself it changed.
 *
 * What changed is the diff with the version before it, so a mark can only ever name a real change;
 * the reason is the stored note for that item. `null` for a version with no readable notes, which is
 * one no reconciler wrote.
 */
export const describeReconciliation = <Kind extends DraftArtifactKind>({
  kind,
  version,
  previous,
  current,
  notes,
}: {
  kind: Kind;
  version: number;
  previous: ArtifactContentByKind[Kind];
  current: ArtifactContentByKind[Kind];
  notes: ArtifactChangeNotes | null;
}): ReconciliationView | null => {
  if (notes === null) {
    return null;
  }

  const diff = diffArtifactContent(kind, previous, current);
  const reasons = new Map(notes.items.map((item) => [item.id, item.reason]));
  const item = (
    change: ReconciliationItem['change'],
    entry: { id: string; label: string },
  ): ReconciliationItem => ({
    id: entry.id,
    change,
    label: entry.label,
    reason: reasons.get(entry.id) ?? null,
  });

  return {
    version,
    upstreamKinds: notes.upstream_kinds,
    unchanged: notes.unchanged,
    items: [
      ...diff.changed.map((entry) => item('changed', entry)),
      ...diff.added.map((entry) => item('added', entry)),
      ...diff.removed.map((entry) => item('removed', entry)),
    ],
    ...(diff.scalars.length === 0
      ? {}
      : { changedFields: diff.scalars.map((field) => field.field) }),
  };
};

/** What each upstream is called in a reconciled version's line, one place for all three steps. */
const RECONCILED_UPSTREAM_WORDING: Record<StaleUpstreamKind, string> = {
  goal: 'goal',
  materials: 'materials',
  rqs: 'questions',
  dg: 'guide',
  audience: 'audience',
};

const joined = (words: readonly string[]): string => {
  const last = words.at(-1);
  if (last === undefined) {
    return '';
  }

  return words.length === 1 ? last : `${words.slice(0, -1).join(', ')} and ${last}`;
};

/**
 * "your changed goal and questions": the upstream kinds a reconciliation answers, or is waiting to
 * answer, in the order a change travels, as every step's reconciliation copy names them; "your
 * earlier changes" when no kind is known.
 */
export const changedUpstreamText = (kinds: readonly StaleUpstreamKind[]): string =>
  kinds.length === 0
    ? 'your earlier changes'
    : `your changed ${joined(upstreamOrder(kinds).map((kind) => RECONCILED_UPSTREAM_WORDING[kind]))}`;

/**
 * The version line and history line of a reconciled version: "Updated for your changed questions and
 * materials", or for one that changed nothing, "Checked against your changed goal; nothing needed to
 * change".
 */
export const reconciledVersionText = (
  notes: Pick<ArtifactChangeNotes, 'upstream_kinds' | 'unchanged'> | null,
): string => {
  if (notes === null) {
    return 'Updated for your earlier changes';
  }

  const changed = changedUpstreamText(notes.upstream_kinds);

  return notes.unchanged
    ? `Checked against ${changed}; nothing needed to change`
    : `Updated for ${changed}`;
};
